import { GlobalStyle, Reset } from "@creditsafe/ui-components";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "react-query";
import { ThemeProvider } from "styled-components";

import type { KycRedirect, UppState, UppUseHistoryObject } from "./App";
import {
  useCheckUserServiceLines,
  useGetFeatureFlags,
} from "./api/user/user-api";
import { useGetUserPermissions } from "./api/permissions/permissions-api";
import { muiTheme, theme } from "./assets/theme";
import LoginPageLoader from "./components/LoginPageLoader";
import type { CompanyReportData } from "./modules/profiles/types/companyReport.type";
import RouteComponents from "./routes";
import type { KycFeatureSettings } from "./store/UserSlice";
import {
  syncUserDataFromUpp,
  updateFeatureFlags,
  updateUserServiceLines,
  updateUserServiceLoaded,
} from "./store/UserSlice";
import type { RootState } from "./store/store";
import {
  resetPermissions,
  setPermissions,
  setPermissionsTeamId,
  setPreferredTeamId,
} from "./store/PermissionsSlice";
import { useLocation } from "react-router";
import { useGetNotifications } from "./api/notifications";
import {
  NotificationSubCategory,
  addNotifications,
} from "./store/NotificationsSlice";
import { ReactQueryDevtools } from "react-query/devtools";
import config from "./config";
import GlobalAlertNotificationsStacks from "./GlobalAlertNotificationsStacks";
import DataProvider from "./DataProvider";
import {
  features,
  MonitorSearchType,
} from "./shared/constants/common.constant";
import { AmlSearchProvider } from "./modules/aml/aml-search-container/AmlSearchContext";
import type { SearchValues } from "./modules/aml/types/aml.type";
import { MonitorSearchProvider } from "./modules/monitor/monitoring-search/MonitorSearchContext";
import type {
  MonitorSearchValues,
  MonitorSearchFilterForm,
} from "./modules/monitor/monitor.type";
import type { UseFormReturn } from "react-hook-form";
import { useGetTeamPreference } from "./api/teams/teams-api";
import { invalidateProfilesIfTeamChanged } from "./api/profile/profiles-api";
import {
  applyActiveTeamToPermissions,
  getActiveTeamId,
  seedPreferredLoginTeam,
  setActiveTeamId,
  setLastKnownActiveTeamName,
} from "./utils/active-team/active-team";
import { activeTeamIdForNewTabs } from "./shared/constants/localstorage.constant";
import TeamAccessLostModal from "./modules/global-settings/user-configurations/current-team-selected/TeamAccessLostModal";

export interface AppLayoutProps {
  isUpp?: boolean;
  uppState?: UppState;
  pushHistoryAction?: (_path: string) => void;
  clearKycRedirectAction: () => void;
}

export interface ReportComponentContextProps {
  companyReport: CompanyReportData;
  uppUseHistoryObject: UppUseHistoryObject;
  kycRedirect: KycRedirect;
  clearKycRedirectAction: () => void;
}

export const ReportContext = createContext<ReportComponentContextProps>(
  {} as ReportComponentContextProps
);

export const AppLayout: React.FC<AppLayoutProps> = ({
  isUpp,
  uppState,
  pushHistoryAction,
  clearKycRedirectAction,
}: AppLayoutProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [lastSearch, setLastSearch] = useState<SearchValues | undefined>();

  const [monitorSearchData, setMonitorSearchData] =
    useState<MonitorSearchValues>({
      name: "",
      firstName: "",
      middleName: "",
      lastName: "",
      searchType: MonitorSearchType.Both,
    });
  const [monitorHasSearched, setMonitorHasSearched] = useState(false);
  const [monitorSearchForm, setMonitorSearchForm] =
    useState<UseFormReturn<MonitorSearchValues> | null>(null);
  const [monitorSearchFilterForm, setMonitorSearchFilterForm] =
    useState<UseFormReturn<MonitorSearchFilterForm> | null>(null);
  const [monitorCurrentPageNumber, setMonitorCurrentPageNumber] = useState(1);

  const location = useLocation();

  useEffect(() => {
    const handleActiveTeamStorageChange = (event: StorageEvent): void => {
      if (
        event.key !== activeTeamIdForNewTabs ||
        !event.newValue ||
        event.newValue === getActiveTeamId()
      ) {
        return;
      }

      setActiveTeamId(event.newValue, { isUserOverride: true });
      dispatch(setPermissionsTeamId(event.newValue));
      void queryClient.invalidateQueries();
    };

    window.addEventListener("storage", handleActiveTeamStorageChange);
    return () =>
      window.removeEventListener("storage", handleActiveTeamStorageChange);
  }, [dispatch, queryClient]);

  const isSignedIn = useSelector((state: RootState) => state.user.isSignedIn);

  // currently we are using multiple api calls to get the feature flags , we need to change this after the api is updated to return all the feature flags in a single call
  const { data: featureFlagFullAmlPdf } = useGetFeatureFlags(
    features.fullAmlPdf
  );
  const { data: featureFlagFilterByDob } = useGetFeatureFlags(
    features.filterByDob
  );
  const { data: featureFlagMonitorSearch } = useGetFeatureFlags(
    features.monitorSearch
  );
  const { data: featureFlagBusinessAndIndividualLink } = useGetFeatureFlags(
    features.businessAndIndividualLink
  );
  const { data: featureFlagMonitorSearchResultSubtab } = useGetFeatureFlags(
    features.monitorSearchResultSubtab
  );
  const { data: featureFlagDenmarkUBOMapping } = useGetFeatureFlags(
    features.denmarkUBOMapping
  );
  const { data: featureFlagSwedenUBOMapping } = useGetFeatureFlags(
    features.swedenUBOMapping
  );
  const { data: featureFlagRechargeSummary } = useGetFeatureFlags(
    features.rechargeSummary
  );
  const { data: featureFlagEnableAuthorization } = useGetFeatureFlags(
    features.enableAuthorization
  );
  const { data: featureFlagEmailManagement } = useGetFeatureFlags(
    features.emailManagement
  );
  const { data: featureFlagProfilesExport } = useGetFeatureFlags(
    features.profilesExport
  );

  const permissionsFetchEnabled = Boolean(featureFlagEnableAuthorization);

  const { data: teamPreferenceData, isFetched: isTeamPreferenceFetched } =
    useGetTeamPreference({
      enabled: permissionsFetchEnabled,
    });

  const lastProfilesTeamIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!permissionsFetchEnabled || !isTeamPreferenceFetched) {
      return;
    }

    seedPreferredLoginTeam(teamPreferenceData?.teamId);
    invalidateProfilesIfTeamChanged(
      queryClient,
      lastProfilesTeamIdRef,
      getActiveTeamId()
    );

    if (teamPreferenceData?.teamName && teamPreferenceData?.teamId) {
      setLastKnownActiveTeamName(
        teamPreferenceData.teamName,
        teamPreferenceData.teamId
      );
    }
    const preferredId = teamPreferenceData?.teamId?.length
      ? teamPreferenceData.teamId
      : null;
    dispatch(setPreferredTeamId(preferredId));
  }, [
    permissionsFetchEnabled,
    isTeamPreferenceFetched,
    teamPreferenceData?.teamId,
    teamPreferenceData?.teamName,
    dispatch,
    queryClient,
  ]);

  const { data: permissionsData } = useGetUserPermissions({
    enabled: permissionsFetchEnabled && isTeamPreferenceFetched,
  });

  useEffect(() => {
    if (!permissionsFetchEnabled || !permissionsData) {
      return;
    }

    // Wait until preferred-login team has settled (success or error).
    // isLoading is false while the query is idle/disabled, which can seed
    // permissions before preference is available.
    if (!isTeamPreferenceFetched) {
      return;
    }

    const resolvedPermissions = applyActiveTeamToPermissions(
      permissionsData,
      teamPreferenceData?.teamId
    );

    dispatch(setPermissions(resolvedPermissions));

    invalidateProfilesIfTeamChanged(
      queryClient,
      lastProfilesTeamIdRef,
      resolvedPermissions.teamId ?? null
    );
  }, [
    permissionsFetchEnabled,
    permissionsData,
    teamPreferenceData?.teamId,
    isTeamPreferenceFetched,
    dispatch,
    queryClient,
  ]);

  useEffect(() => {
    if (!permissionsFetchEnabled) {
      dispatch(resetPermissions());
    }
  }, [permissionsFetchEnabled, dispatch]);

  useEffect(() => {
    if (pushHistoryAction) {
      pushHistoryAction(`${location.pathname}${location.search}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const kycServiceLinesState = useSelector(
    (state: RootState) => state.user.services
  );

  const checkUserServiceLines = useCheckUserServiceLines(kycServiceLinesState);

  useEffect(() => {
    // Check if all queries are either successful or resulted in a 403 error
    const allQueriesCompleted = checkUserServiceLines.every(
      (result) => result.isSuccess || result.isError
    );

    if (allQueriesCompleted) {
      // Process each successful result
      checkUserServiceLines.forEach((result, index) => {
        if (result.isSuccess && result.data) {
          if (
            result.data.service.serviceId ===
            kycServiceLinesState[index].serviceId
          ) {
            dispatch(updateUserServiceLines(result.data));
          }
        }
      });

      // Dispatch updateUserServiceLoaded regardless of individual query success
      dispatch(updateUserServiceLoaded());
    }
  }, [checkUserServiceLines, dispatch, kycServiceLinesState]);

  useEffect(() => {
    if (isUpp) {
      const restOfUppState = { ...uppState };

      delete restOfUppState.uppUseHistoryObject;

      dispatch(syncUserDataFromUpp({ restOfUppState, isUpp }));
    }
  }, [dispatch, isUpp, uppState]);

  const { data: notificationResponse } = useGetNotifications({
    category: ["profile"],
    subCategory: NotificationSubCategory.Report,
  });

  useEffect(() => {
    if (notificationResponse?.items?.length) {
      const notificationsWithSeenFlag = notificationResponse.items.map(
        (item) => ({
          ...item,
          isNotified: false,
        })
      );

      dispatch(addNotifications(notificationsWithSeenFlag));
    }
  }, [notificationResponse?.items, dispatch]);

  useEffect(() => {
    const allFlagsDefined =
      featureFlagFullAmlPdf !== undefined &&
      featureFlagFilterByDob !== undefined;

    if (!allFlagsDefined) return;

    const getFlagSetting = (
      flag: [] | undefined | boolean,
      key: keyof KycFeatureSettings
    ): KycFeatureSettings => {
      const value: "on" | "off" = flag ? "on" : "off";
      return {
        [key]: value,
      };
    };

    const featureSettings: KycFeatureSettings[] = [
      getFlagSetting(featureFlagFullAmlPdf, features.fullAmlPdf),
      getFlagSetting(featureFlagFilterByDob, features.filterByDob),
      getFlagSetting(featureFlagMonitorSearch, features.monitorSearch),
      getFlagSetting(
        featureFlagBusinessAndIndividualLink,
        features.businessAndIndividualLink
      ),
      getFlagSetting(
        featureFlagMonitorSearchResultSubtab,
        features.monitorSearchResultSubtab
      ),
      getFlagSetting(featureFlagDenmarkUBOMapping, features.denmarkUBOMapping),
      getFlagSetting(featureFlagSwedenUBOMapping, features.swedenUBOMapping),
      getFlagSetting(featureFlagRechargeSummary, features.rechargeSummary),
      getFlagSetting(
        featureFlagEnableAuthorization,
        features.enableAuthorization
      ),
      getFlagSetting(featureFlagEmailManagement, features.emailManagement),
      getFlagSetting(featureFlagProfilesExport, features.profilesExport),
    ];

    dispatch(updateFeatureFlags(featureSettings));
  }, [
    featureFlagFullAmlPdf,
    featureFlagFilterByDob,
    featureFlagMonitorSearch,
    featureFlagBusinessAndIndividualLink,
    featureFlagMonitorSearchResultSubtab,
    featureFlagDenmarkUBOMapping,
    featureFlagSwedenUBOMapping,
    featureFlagRechargeSummary,
    featureFlagEnableAuthorization,
    featureFlagEmailManagement,
    featureFlagProfilesExport,
    dispatch,
  ]);

  const reportContextContextValue = useMemo(
    () => ({
      companyReport: {
        ...(uppState?.companyReport as CompanyReportData),
        fileName: uppState?.fileName,
        uppLanguage: uppState?.i18n?.language?.split("-")[0],
      },
      uppUseHistoryObject: uppState?.uppUseHistoryObject as UppUseHistoryObject,
      kycRedirect: uppState?.kycRedirect as KycRedirect,
      clearKycRedirectAction,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uppState]
  );

  const checkUppSignIn = (): React.ReactNode => {
    if (isUpp) {
      return (
        <DataProvider>
          <ReportContext.Provider value={reportContextContextValue}>
            <DataProvider>
              <AmlSearchProvider value={{ lastSearch, setLastSearch }}>
                <MonitorSearchProvider
                  value={{
                    searchData: monitorSearchData,
                    setSearchData: setMonitorSearchData,
                    hasSearched: monitorHasSearched,
                    setHasSearched: setMonitorHasSearched,
                    monitorSearchForm: monitorSearchForm,
                    setMonitorSearchForm: setMonitorSearchForm,
                    monitorSearchFilterForm: monitorSearchFilterForm,
                    setMonitorSearchFilterForm: setMonitorSearchFilterForm,
                    currentPageNumber: monitorCurrentPageNumber,
                    setCurrentPageNumber: setMonitorCurrentPageNumber,
                  }}
                >
                  <RouteComponents isUpp={isUpp} />
                </MonitorSearchProvider>
              </AmlSearchProvider>
            </DataProvider>
            {config.REACT_QUERY_DEBUG_MODE && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
          </ReportContext.Provider>
        </DataProvider>
      );
    }
    return isSignedIn ? (
      <DataProvider>
        <AmlSearchProvider value={{ lastSearch, setLastSearch }}>
          <MonitorSearchProvider
            value={{
              searchData: monitorSearchData,
              setSearchData: setMonitorSearchData,
              hasSearched: monitorHasSearched,
              setHasSearched: setMonitorHasSearched,
              monitorSearchForm: monitorSearchForm,
              setMonitorSearchForm: setMonitorSearchForm,
              monitorSearchFilterForm: monitorSearchFilterForm,
              setMonitorSearchFilterForm: setMonitorSearchFilterForm,
              currentPageNumber: monitorCurrentPageNumber,
              setCurrentPageNumber: setMonitorCurrentPageNumber,
            }}
          >
            <RouteComponents isUpp={isUpp} />
          </MonitorSearchProvider>
        </AmlSearchProvider>
      </DataProvider>
    ) : (
      <LoginPageLoader />
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <MuiThemeProvider theme={muiTheme}>
        <Reset />
        <GlobalStyle />
        {checkUppSignIn()}
        <TeamAccessLostModal
          enabled={(isSignedIn || Boolean(isUpp)) && permissionsFetchEnabled}
        />
        <GlobalAlertNotificationsStacks />
      </MuiThemeProvider>
    </ThemeProvider>
  );
};
